import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bot, Users, TrendingUp, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Stats {
  total_contacts: number;
  enrolled: number;
  not_enrolled: number;
  prospects_enrolled: number;
}

export interface NanocorpPipelineWidgetProps {
  /** Called by parent CRM after enroll-all to refetch contacts (so badges update). */
  onChange?: () => void;
}

export default function NanocorpPipelineWidget({ onChange }: NanocorpPipelineWidgetProps) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/nanocorp/pipeline/stats`, { credentials: "include" });
      if (r.ok) setStats(await r.json());
    } catch { /* tolerate */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const enrollAll = async () => {
    setEnrolling(true);
    try {
      const r = await fetch(`${API_URL}/api/nanocorp/pipeline/enroll-all`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`${d.just_enrolled} nouveaux contacts inscrits dans le pipeline NanoCorp`);
        await load();
        onChange?.();
      } else {
        toast.error("Inscription échouée");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setEnrolling(false);
  };

  if (loading) {
    return (
      <Card className="border-primary/20 bg-card/40">
        <CardContent className="py-4 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }
  if (!stats) return null;

  const pct = stats.total_contacts > 0 ? Math.round((stats.enrolled / stats.total_contacts) * 100) : 0;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-card/40 to-card/20" data-testid="nanocorp-pipeline-widget">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pipeline NanoCorp</p>
              <p className="text-[10px] text-muted-foreground">Les agents IA travaillent sur tes contacts inscrits</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs" data-testid="nanocorp-stats">
              <span className="flex items-center gap-1"><Users className="h-3 w-3 text-sky-400" /> <b className="text-foreground">{stats.enrolled}</b><span className="text-muted-foreground">/{stats.total_contacts}</span></span>
              <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3 text-amber-400" /> <b className="text-foreground">{stats.prospects_enrolled}</b> prospects actifs</span>
              <span className="text-emerald-400 text-[10px] font-semibold">{pct}%</span>
            </div>
            {stats.not_enrolled > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={enrollAll}
                disabled={enrolling}
                className="h-7 text-xs gap-1"
                data-testid="enroll-all-btn"
              >
                {enrolling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                Inscrire les {stats.not_enrolled} restants
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
